// Live stream API (backend/reports_studio/api/routers/live.py) + WebSocket event hook.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { API_BASE, api } from "../../api/client";
import type { ArtifactDict, LaneNode, OverlayResponse, SeriesTile, SnapshotDict } from "../../api/types";

export interface SessionStats {
  messages: number;
  bytes: number;
  first_at?: string | null;
  last_at?: string | null;
  last_sequence: number;
  last_frame_index: number;
  wps?: number | null;
  gaps: number;
  missing_frames: number;
  seq_resets: number;
  segments: number;
  errors: number;
  latency_ms?: number | null;
  rate_hz?: number | null;
}

export interface SegmentDict {
  flight_id: string;
  started_at: string;
  start_frame_index: number;
  first_sequence: number;
  wps: number;
  run_id?: string | null;
  frames: number;
  ticks: number;
  fires: number;
  artifacts: number;
  errors: string[];
  status: "active" | "finishing" | "done" | "failed" | "discarded";
  reason?: string | null;
  resumed: boolean;
  finished_at?: string | null;
  checkpoint_key?: string | null;
  checkpoints: number;
  snapshots?: number;
}

export type SessionStatus = "active" | "finishing" | "waiting" | "idle" | "unbound" | "done" | "failed" | "discarded";

export interface LiveSessionSummary {
  key: string;
  device_id: string;
  session_id: string;
  tenant?: string | null;
  tail?: string | null;
  project_id?: string | null;
  project_name?: string | null;
  binding_id?: string | null;
  status: SessionStatus;
  bound: boolean;
  t: number;
  created_at: string;
  stats: SessionStats;
  segment?: SegmentDict | null;
  fires: number;
  resample?: string | null;
}

export interface LiveTriggerInfo {
  name: string;
  reports: string[];
  depends_on: string[];
  end_of_flight: boolean;
  declared_end_of_flight: boolean;
  armed?: boolean | null;
  failed?: boolean | null;
  fires: number;
}

export interface LiveEventDict {
  trigger: string;
  t: number;
  kind: string;
  message: string;
  data?: Record<string, unknown>;
}

export interface LiveSessionDetail extends LiveSessionSummary {
  triggers: LiveTriggerInfo[];
  snapshots: SnapshotDict[];
  events: LiveEventDict[];
  artifacts: (ArtifactDict & { path?: string | null; storage_key?: string | null; deliveries?: number })[];
  log: string[];
  history: SegmentDict[];
  mnemonics: string[];
  live?: LiveSpec | null;
}

export interface LiveSpec {
  idle_seconds: number;
  max_gap_frames: number;
  min_seconds: number;
  checkpoint_frames: number;
  flight_start?: string | null;
  flight_end?: string | null;
  end_hold_seconds: number;
  trace: "off" | "bool" | "all";
  deliver: boolean;
  render: boolean;
}

export interface LiveBinding {
  id: string;
  tenant: string;
  device: string;
  project_id: string;
  tail?: string | null;
  label: string;
  enabled: boolean;
  deliver?: boolean | null;
  trace?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DemoStatus {
  device_id: string;
  session_id: string;
  subject: string;
  frames: number;
  published: number;
  loops: number;
  speed: number;
  loop: boolean;
  running: boolean;
  wps: number;
  error?: string | null;
}

export interface TransportStatus {
  kind: "off" | "nats";
  url?: string;
  subject?: string;
  mode?: string;
  connected?: boolean;
  messages?: number;
  reconnects?: number;
  last_error?: string | null;
  last_message_at?: string | null;
  reason?: string;
  running?: boolean;
}

export interface LiveStatus {
  running: boolean;
  started_at: string;
  transport: TransportStatus;
  sessions: number;
  active: number;
  waiting: number;
  unbound: number;
  received: number;
  dropped: number;
  parse_errors: number;
  queue: number;
  bus: string;
  checkpoints: string;
  demos: DemoStatus[];
  bindings: LiveBinding[];
}

export interface LiveSeriesResponse {
  key: string;
  t: number;
  total_seconds: number;
  flight_id?: string | null;
  run_id?: string | null;
  series: Record<string, SeriesTile & { last?: number | null }>;
  missing: string[];
}

export interface LiveLanesResponse {
  key: string;
  run_id?: string | null;
  flight_id?: string | null;
  total_seconds: number;
  mode: string;
  nodes: LaneNode[];
  snapshot_ticks: [string, number][];
}

export type LiveEventType =
  | "hello"
  | "heartbeat"
  | "pong"
  | "session"
  | "segment_start"
  | "frame"
  | "snapshot"
  | "artifact"
  | "event"
  | "segment_end"
  | "log"
  | "demo"
  | "transport";

export interface LiveEvent {
  type: LiveEventType;
  ts?: string;
  key?: string;
  tenant?: string | null;
  device_id?: string;
  session_id?: string;
  flight_id?: string | null;
  run_id?: string | null;
  t?: number;
  frames?: number;
  fires?: number;
  frame_index?: number;
  latency_ms?: number | null;
  session?: LiveSessionSummary;
  segment?: SegmentDict;
  snapshot?: SnapshotDict;
  artifact?: ArtifactDict & { path?: string | null };
  event?: LiveEventDict;
  level?: "info" | "warning" | "error";
  message?: string;
  status?: LiveStatus | DemoStatus;
  transport?: TransportStatus;
  resumed?: boolean;
  [k: string]: unknown;
}

export const liveKeys = {
  status: ["live", "status"] as const,
  sessions: ["live", "sessions"] as const,
  session: (k: string) => ["live", "session", k] as const,
  series: (k: string, p: string, n: number) => ["live", "series", k, p, n] as const,
  lanes: (k: string, trig: string) => ["live", "lanes", k, trig] as const,
  bindings: ["live", "bindings"] as const,
  history: ["live", "history"] as const,
};

export const useLiveStatus = (enabled = true) =>
  useQuery({ queryKey: liveKeys.status, queryFn: () => api.get<LiveStatus>("/live/status"), enabled, refetchInterval: 5000, retry: false });

export const useLiveSessions = () =>
  useQuery({
    queryKey: liveKeys.sessions,
    queryFn: () => api.get<{ sessions: LiveSessionSummary[] }>("/live/sessions"),
    refetchInterval: 4000,
    retry: false,
  });

export const useLiveSession = (key: string | null) =>
  useQuery({
    queryKey: liveKeys.session(key ?? ""),
    queryFn: () => api.get<LiveSessionDetail>(`/live/sessions/${encodeURIComponent(key ?? "")}`),
    enabled: !!key,
    retry: false,
  });

export const useLiveHistory = () =>
  useQuery({ queryKey: liveKeys.history, queryFn: () => api.get<{ sessions: Record<string, unknown>[] }>("/live/sessions/history"), retry: false });

export const useLiveSeries = (key: string | null, params: string[], points = 1500) => {
  const p = params.join(",");
  return useQuery({
    queryKey: liveKeys.series(key ?? "", p, points),
    queryFn: () => api.get<LiveSeriesResponse>(`/live/sessions/${encodeURIComponent(key ?? "")}/series?params=${encodeURIComponent(p)}&points=${points}`),
    enabled: !!key && params.length > 0,
    retry: false,
    placeholderData: (prev) => prev,
  });
};

export const useLiveLanes = (key: string | null, trigger: string | null) =>
  useQuery({
    queryKey: liveKeys.lanes(key ?? "", trigger ?? ""),
    queryFn: () => api.get<LiveLanesResponse>(`/live/sessions/${encodeURIComponent(key ?? "")}/lanes${trigger ? `?trigger=${encodeURIComponent(trigger)}` : ""}`),
    enabled: !!key,
    retry: false,
    placeholderData: (prev) => prev,
  });

export const fetchLiveOverlay = (key: string, t: number | null, trigger?: string | null) =>
  api.get<OverlayResponse & { trace: boolean }>(
    `/live/sessions/${encodeURIComponent(key)}/overlay?${t != null ? `t=${t}&` : ""}${trigger ? `trigger=${encodeURIComponent(trigger)}` : ""}`,
  );

export const useLiveBindings = () =>
  useQuery({ queryKey: liveKeys.bindings, queryFn: () => api.get<{ bindings: LiveBinding[] }>("/live/bindings"), retry: false });

export function usePutBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: Partial<LiveBinding> & { device: string; project_id: string }) => api.put<LiveBinding>("/live/bindings", b),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: liveKeys.bindings });
      qc.invalidateQueries({ queryKey: liveKeys.status });
      qc.invalidateQueries({ queryKey: liveKeys.sessions });
    },
  });
}

export function useDeleteBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/live/bindings/${encodeURIComponent(id)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: liveKeys.bindings });
      qc.invalidateQueries({ queryKey: liveKeys.status });
    },
  });
}

export function useStartDemo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { flight_id?: string; path?: string; device_id: string; session_id?: string; speed: number; loop: boolean; project_id?: string; tail?: string }) =>
      api.post<DemoStatus>("/live/demo", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: liveKeys.status });
      qc.invalidateQueries({ queryKey: liveKeys.sessions });
      qc.invalidateQueries({ queryKey: liveKeys.bindings });
    },
  });
}

export function useStopDemo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (device_id?: string) => api.del<{ stopped: number }>(`/live/demo${device_id ? `?device_id=${encodeURIComponent(device_id)}` : ""}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: liveKeys.status }),
  });
}

export function useEndSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, reason }: { key: string; reason?: string }) =>
      api.post<{ ended: boolean }>(`/live/sessions/${encodeURIComponent(key)}/end${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: liveKeys.sessions });
      qc.invalidateQueries({ queryKey: liveKeys.session(v.key) });
    },
  });
}

export function useForgetSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.del<{ deleted: boolean }>(`/live/sessions/${encodeURIComponent(key)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: liveKeys.sessions }),
  });
}

export function liveSocketUrl(params: Record<string, string | undefined>): string {
  const base = API_BASE.startsWith("http") ? new URL(API_BASE) : new URL(API_BASE, window.location.origin);
  base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
  const url = new URL(`${base.pathname.replace(/\/$/, "")}/live/events`, base);
  for (const [k, v] of Object.entries(params)) if (v) url.searchParams.set(k, v);
  return url.toString();
}

export type SocketState = "connecting" | "open" | "closed" | "error";

/**
 * Subscribe to the live event bus. `onEvent` is called for every event (already filtered
 * server-side by `key`/`types`); reconnects with backoff while mounted.
 */
export function useLiveEvents(opts: { key?: string | null; types?: LiveEventType[]; replay?: number; enabled?: boolean; onEvent: (e: LiveEvent) => void }) {
  const { key, types, replay = 50, enabled = true } = opts;
  const handler = useRef(opts.onEvent);
  handler.current = opts.onEvent;
  const [state, setState] = useState<SocketState>("closed");
  const [lastError, setLastError] = useState<string | null>(null);
  const sockRef = useRef<WebSocket | null>(null);
  const typesKey = (types ?? []).join(",");

  useEffect(() => {
    if (!enabled) return;
    let closed = false;
    let attempt = 0;
    let timer: number | null = null;
    let ping: number | null = null;

    const connect = () => {
      if (closed) return;
      setState("connecting");
      const ws = new WebSocket(liveSocketUrl({ key: key ?? undefined, types: typesKey || undefined, replay: String(replay) }));
      sockRef.current = ws;
      ws.onopen = () => {
        attempt = 0;
        setState("open");
        setLastError(null);
        ping = window.setInterval(() => ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ op: "ping", ts: Date.now() })), 20_000);
      };
      ws.onmessage = (m) => {
        try {
          handler.current(JSON.parse(m.data as string) as LiveEvent);
        } catch {
          /* ignore junk */
        }
      };
      ws.onerror = () => {
        setState("error");
        setLastError("socket error");
      };
      ws.onclose = (ev) => {
        if (ping) window.clearInterval(ping);
        ping = null;
        sockRef.current = null;
        if (closed) return;
        setState("closed");
        if (ev.code === 4401) {
          setLastError("not authenticated");
          return;
        }
        if (ev.code === 4404) {
          setLastError("live streaming disabled");
          return;
        }
        attempt += 1;
        timer = window.setTimeout(connect, Math.min(15_000, 500 * 2 ** Math.min(attempt, 5)));
      };
    };
    connect();
    return () => {
      closed = true;
      if (timer) window.clearTimeout(timer);
      if (ping) window.clearInterval(ping);
      sockRef.current?.close();
      sockRef.current = null;
    };
  }, [key, typesKey, replay, enabled]);

  const send = (msg: Record<string, unknown>) => {
    const ws = sockRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  };
  return { state, lastError, send };
}
